/**
 * Charts you can point at, without changing what gets printed.
 *
 * The obvious way to get interactive charts is to draw them a second time with
 * a charting library on screen. That gives you two chart implementations that
 * drift, and a screen that no longer matches the file — which is the one thing
 * this product is for. So the drawing does not change at all: the same SVG the
 * PDF contains is the SVG the browser shows, and this adds a hover, a focus
 * ring and a tooltip on top of the marks that are already there.
 *
 * The chart block records what each mark stands for as it draws
 * (`data-bp-chart` on the wrapper, `data-bp-hit="<index>"` on each mark), so
 * nothing here re-derives any geometry: it reads the real element's own box.
 * The PDF translator strips those attributes, so print output is unchanged.
 *
 * Everything it adds is inert: no layout is read or written that could affect
 * pagination, and `destroy()` puts the DOM back exactly as it found it.
 */

/** One mark a reader can point at. Mirrors `ChartHit` in the blocks package. */
interface Hit {
  categoryIndex: number;
  category: string;
  seriesIndex: number;
  series: string;
  value: number | null;
  formatted: string;
  color: string;
  share?: number;
}

interface ChartPayload {
  type: string;
  title: string;
  hits: Hit[];
}

export interface ChartInteractionOptions {
  /**
   * Builds the tooltip's content. Return a string (shown as text) or an
   * element. The default names the category, the series and the value.
   */
  tooltip?(hit: Hit, context: { type: string; title: string; siblings: Hit[] }): string | HTMLElement;
  /** Called when a mark is clicked or activated from the keyboard. */
  onSelect?(hit: Hit, event: Event): void;
  /**
   * Replaces the whole thing for one chart. Return `false` to leave that chart
   * alone — the escape hatch for a host that would rather mount its own
   * charting library over the box. The SVG underneath is still what prints.
   */
  onChart?(element: HTMLElement, payload: ChartPayload): boolean | void;
  /** Where the tooltip is appended. Defaults to the document body. */
  portal?: HTMLElement;
}

const STYLE_ID = "bp-chart-interactions";

const CSS = `
.bp-chart [data-bp-hit] { cursor: default; transition: opacity 120ms ease, filter 120ms ease; }
.bp-chart--active [data-bp-hit] { opacity: 0.45; }
.bp-chart--active [data-bp-hit][data-bp-on] { opacity: 1; filter: brightness(1.06); }
.bp-chart [data-bp-hit]:focus-visible { outline: none; }
.bp-chart [data-bp-hit][data-bp-focus] { stroke: #111827; stroke-width: 2; paint-order: stroke; }
.bp-chart-tip {
  position: fixed; z-index: 2147483000; pointer-events: none;
  max-width: 260px; padding: 6px 9px; border-radius: 6px;
  background: #111827; color: #fff;
  font: 12px/1.45 system-ui, -apple-system, "Segoe UI", sans-serif;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.28);
  opacity: 0; transform: translateY(2px); transition: opacity 90ms ease, transform 90ms ease;
}
.bp-chart-tip[data-bp-shown] { opacity: 1; transform: none; }
.bp-chart-tip__row { display: flex; align-items: baseline; gap: 6px; white-space: nowrap; }
.bp-chart-tip__row + .bp-chart-tip__row { margin-top: 2px; }
.bp-chart-tip__swatch { width: 8px; height: 8px; border-radius: 2px; flex: none; align-self: center; }
.bp-chart-tip__label { color: #cbd5e1; }
.bp-chart-tip__value { margin-left: auto; font-weight: 600; font-variant-numeric: tabular-nums; }
.bp-chart-tip__head { font-weight: 600; margin-bottom: 3px; }
@media (prefers-reduced-motion: reduce) {
  .bp-chart-tip, .bp-chart [data-bp-hit] { transition: none; }
}
@media print { .bp-chart-tip { display: none !important; } }
`;

function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const el = doc.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  doc.head.appendChild(el);
}

/** The default tooltip: every series at the category the reader is pointing at. */
function defaultTooltip(hit: Hit, ctx: { type: string; title: string; siblings: Hit[] }, doc: Document): HTMLElement {
  const box = doc.createElement("div");
  const head = doc.createElement("div");
  head.className = "bp-chart-tip__head";
  head.textContent = hit.category || ctx.title;
  box.appendChild(head);

  // A pie has one series, so naming it twice would just repeat the category.
  const rows = ctx.type === "pie" || ctx.type === "donut" ? [hit] : ctx.siblings;
  for (const r of rows) {
    const row = doc.createElement("div");
    row.className = "bp-chart-tip__row";

    const swatch = doc.createElement("span");
    swatch.className = "bp-chart-tip__swatch";
    swatch.style.background = r.color;
    row.appendChild(swatch);

    const label = doc.createElement("span");
    label.className = "bp-chart-tip__label";
    label.textContent = ctx.type === "pie" || ctx.type === "donut" ? (r.share !== undefined ? `${Math.round(r.share * 1000) / 10}%` : "") : r.series;
    row.appendChild(label);

    const value = doc.createElement("span");
    value.className = "bp-chart-tip__value";
    // A null is a gap in the data, not a zero, and saying "0" would be a lie.
    value.textContent = r.value === null ? "—" : r.formatted;
    if (r === hit && rows.length > 1) value.style.textDecoration = "underline";
    row.appendChild(value);

    box.appendChild(row);
  }
  return box;
}

/**
 * Makes every chart under `root` respond to a pointer and to the keyboard.
 *
 * Safe to call again after the DOM is replaced — each call returns its own
 * teardown, and calling it twice on the same nodes does not double up.
 */
export function enableChartInteractions(root: HTMLElement, options: ChartInteractionOptions = {}): () => void {
  const doc = root.ownerDocument;
  if (!doc?.defaultView) return () => undefined;
  ensureStyle(doc);

  const portal = options.portal ?? doc.body;
  const tip = doc.createElement("div");
  tip.className = "bp-chart-tip";
  tip.setAttribute("role", "tooltip");
  tip.setAttribute("aria-hidden", "true");

  let tipMounted = false;
  const cleanups: Array<() => void> = [];

  const hideTip = () => {
    tip.removeAttribute("data-bp-shown");
    tip.setAttribute("aria-hidden", "true");
  };

  const showTip = (content: string | HTMLElement, anchor: DOMRect) => {
    if (!tipMounted) {
      portal.appendChild(tip);
      tipMounted = true;
    }
    tip.replaceChildren(typeof content === "string" ? doc.createTextNode(content) : content);
    tip.setAttribute("aria-hidden", "false");
    tip.setAttribute("data-bp-shown", "");
    // Measured after the content is in, and clamped to the viewport so a mark
    // near an edge does not push the tooltip off the screen.
    const box = tip.getBoundingClientRect();
    const view = doc.defaultView!;
    const gap = 10;
    let left = anchor.left + anchor.width / 2 - box.width / 2;
    let top = anchor.top - box.height - gap;
    if (top < 4) top = anchor.bottom + gap;
    left = Math.max(4, Math.min(left, view.innerWidth - box.width - 4));
    top = Math.max(4, Math.min(top, view.innerHeight - box.height - 4));
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  };

  for (const chart of Array.from(root.querySelectorAll<HTMLElement>(".bp-chart[data-bp-chart]"))) {
    if (chart.dataset.bpInteractive === "1") continue;

    let payload: ChartPayload;
    try {
      payload = JSON.parse(chart.dataset.bpChart ?? "") as ChartPayload;
    } catch {
      continue;
    }
    if (!Array.isArray(payload.hits) || payload.hits.length === 0) continue;
    if (options.onChart?.(chart, payload) === false) continue;

    chart.dataset.bpInteractive = "1";
    const marks = Array.from(chart.querySelectorAll<SVGElement>("[data-bp-hit]"));
    if (marks.length === 0) continue;

    // Everything at the same category, which is what a reader comparing two
    // lines actually wants to see.
    const byCategory = new Map<number, Hit[]>();
    for (const hit of payload.hits) {
      const list = byCategory.get(hit.categoryIndex) ?? [];
      list.push(hit);
      byCategory.set(hit.categoryIndex, list);
    }

    const activate = (mark: SVGElement, hit: Hit, focus: boolean) => {
      chart.classList.add("bp-chart--active");
      const siblings = byCategory.get(hit.categoryIndex) ?? [hit];
      for (const m of marks) {
        const h = payload.hits[Number(m.getAttribute("data-bp-hit"))];
        if (h && siblings.includes(h)) m.setAttribute("data-bp-on", "");
        else m.removeAttribute("data-bp-on");
        m.removeAttribute("data-bp-focus");
      }
      if (focus) mark.setAttribute("data-bp-focus", "");
      const content = options.tooltip?.(hit, { type: payload.type, title: payload.title, siblings }) ?? defaultTooltip(hit, { type: payload.type, title: payload.title, siblings }, doc);
      showTip(content, mark.getBoundingClientRect());
    };

    const deactivate = () => {
      chart.classList.remove("bp-chart--active");
      for (const m of marks) {
        m.removeAttribute("data-bp-on");
        m.removeAttribute("data-bp-focus");
      }
      hideTip();
    };

    for (const mark of marks) {
      const hit = payload.hits[Number(mark.getAttribute("data-bp-hit"))];
      if (!hit) continue;

      // Reachable by keyboard and announced, because a chart nobody can tab to
      // is a picture of some numbers.
      mark.setAttribute("tabindex", "0");
      mark.setAttribute("role", "img");
      const share = hit.share !== undefined ? `, ${Math.round(hit.share * 1000) / 10}%` : "";
      mark.setAttribute("aria-label", `${hit.category}${hit.series ? `, ${hit.series}` : ""}: ${hit.value === null ? "no value" : hit.formatted}${share}`);

      const onEnter = () => activate(mark, hit, false);
      const onFocus = () => activate(mark, hit, true);
      const onKey = (e: Event) => {
        const key = (e as KeyboardEvent).key;
        if (key === "Enter" || key === " ") {
          e.preventDefault();
          options.onSelect?.(hit, e);
        } else if (key === "Escape") deactivate();
      };
      const onClick = (e: Event) => options.onSelect?.(hit, e);

      mark.addEventListener("pointerenter", onEnter);
      mark.addEventListener("focus", onFocus);
      mark.addEventListener("keydown", onKey);
      if (options.onSelect) mark.addEventListener("click", onClick);
      cleanups.push(() => {
        mark.removeEventListener("pointerenter", onEnter);
        mark.removeEventListener("focus", onFocus);
        mark.removeEventListener("keydown", onKey);
        mark.removeEventListener("click", onClick);
        mark.removeAttribute("tabindex");
        mark.removeAttribute("role");
        mark.removeAttribute("aria-label");
        mark.removeAttribute("data-bp-on");
        mark.removeAttribute("data-bp-focus");
      });
    }

    chart.addEventListener("pointerleave", deactivate);
    chart.addEventListener("focusout", (e) => {
      // Moving between two marks of the same chart is not leaving it.
      if (!chart.contains((e as FocusEvent).relatedTarget as Node | null)) deactivate();
    });
    cleanups.push(() => {
      chart.classList.remove("bp-chart--active");
      delete chart.dataset.bpInteractive;
    });
  }

  // Scrolling moves the mark out from under a tooltip pinned to the viewport.
  const onScroll = () => hideTip();
  doc.addEventListener("scroll", onScroll, true);
  cleanups.push(() => doc.removeEventListener("scroll", onScroll, true));

  return () => {
    for (const c of cleanups.splice(0)) c();
    tip.remove();
    tipMounted = false;
  };
}
